/**
 * The ONE way a frontend operation mirrors absolute `storeStock` values to Supabase (ATL-765).
 *
 * Every producer of the `products.updateStock` dual-write label MUST go through
 * `sendBulkStockMirror` — `updateProduct`'s mirror, `saveNewProduct`'s initial-stock seed, and
 * `updateStockManually` (firebase.ts). The reason is the outbox's identity model: entries key by
 * `(label, firestoreId)` — the PRODUCT id — which is COARSER than the per-store payloads flowing
 * under it. Any two producers that address the same product with different store subsets can
 * destroy each other's queued retries (a newer failure SUPERSEDES the older entry; a live success
 * PURGES it). This module restores correctness at that granularity by construction:
 *
 *  1. ONE bulk call per product — never one call per store (per-store calls superseded each
 *     other's queued entries; the pre-ATL-765 shape).
 *  2. FOLDING: stores carried by a QUEUED failed write are folded into the next live call with
 *     their CURRENT absolute values, so the purge-on-success can never orphan them. Absolute
 *     semantics make re-sending the latest Firestore value correct under every interleaving.
 *  3. PER-ITEM FAILURES RE-QUEUE: the bulk endpoint returns 200 with `results[].ok` per item
 *     (per-store failure isolation). A plain "return the response" would make
 *     `bestEffortDualWrite` count the call a success and purge the very retries those items
 *     still need — so a response with failed items THROWS a synthetic transient error whose
 *     `requestConfig` carries ONLY the failed items. The outbox queues it (superseding any older
 *     entry — whose non-failed stores just landed live), and the replay retries just the losers.
 *  4. A FAILED REPLAY IS A FAILED REPLAY (ATL-962). The outbox inspects the replayed response's
 *     `results[].ok` exactly as this module does, so a replay that reproduces the failure keeps
 *     the entry, consumes an attempt, and is eventually dropped LOUDLY by `reportDrop` — the same
 *     treatment every other permanent dual-write loss gets. Until ATL-962 the replay's 200 purged
 *     the entry with no console line and no Sentry event; the note that used to sit here called
 *     that acceptable because "a deterministic per-item failure converges after one replay", and
 *     that was simply not true of the causes measured in production (34 of 35 classified events
 *     were `Product not found`, one was `numeric field overflow` — neither converges by retrying).
 *
 * Module placement: this file imports nothing from firebase.ts or products.ts DIRECTLY (both
 * import it). It does reach firebase.ts transitively — typedApiClient imports `auth` from there,
 * a pre-existing call-time-only cycle shared by every API consumer — but adds no module-init
 * edge of its own, which is what lets firebase.ts's `updateStockManually` share the exact same
 * path safely.
 */

import { bestEffortDualWrite } from './dualWrite'
import { getOutboxSnapshot } from './dualWriteOutbox'
import {
  BULK_STOCK_MIRROR_URL,
  failedBulkStockItems,
  reportFailedStockMirrorItems,
} from './stockMirrorResult'
import { parseDecimalValue } from '../../utils/ingredientQuantity'
import { productsApi } from '../API/typedApiClient'

interface StockMirrorItem {
  storeId: string
  newStock: number
}

/**
 * Store ids carried by QUEUED (failed, not yet replayed) stock writes for this product.
 * Reads both request shapes: the pre-bulk single-store `{storeId}` body (entries persisted by an
 * older bundle survive an OTA) and the bulk `{updates: [...]}` body.
 */
async function queuedStockMirrorStoreIds(productId: string): Promise<ReadonlySet<string>> {
  const ids = new Set<string>()
  try {
    for (const entry of await getOutboxSnapshot()) {
      if (entry.label !== 'products.updateStock' || entry.firestoreId !== productId) continue
      const data = entry.request?.data as
        | { storeId?: unknown; updates?: Array<{ storeId?: unknown }> }
        | undefined
      if (typeof data?.storeId === 'string') ids.add(data.storeId)
      for (const item of Array.isArray(data?.updates) ? data.updates : []) {
        if (typeof item?.storeId === 'string') ids.add(item.storeId)
      }
    }
  } catch {
    // Snapshot failure must never block the live mirror; worst case is the pre-fold behavior.
  }
  return ids
}

/**
 * Mirror absolute stock values for ONE product to Supabase in one bulk call.
 *
 * `foldSource` is the map of current absolute values (`storeStock`-shaped, possibly raw strings)
 * used to fold queued stores in; pass `null` only when no queued entry can exist for this
 * product id (a freshly created product). A store found in the queue but absent from
 * `foldSource`, or whose value is uncoercible, is left out — its queued entry is superseded, and
 * that is the accepted bound: nothing may be invented (the ATL-706/714 discipline).
 */
export async function sendBulkStockMirror(
  productId: string,
  items: ReadonlyArray<StockMirrorItem>,
  foldSource: Record<string, unknown> | null,
): Promise<void> {
  const updates: StockMirrorItem[] = [...items]

  if (foldSource) {
    const queuedStores = await queuedStockMirrorStoreIds(productId)
    if (queuedStores.size > 0) {
      const planned = new Set(updates.map((u) => u.storeId))
      for (const storeId of queuedStores) {
        if (planned.has(storeId) || !(storeId in foldSource)) continue
        const value = parseDecimalValue(foldSource[storeId])
        if (value === null) continue
        updates.push({ storeId, newStock: value })
      }
    }
  }

  if (updates.length === 0) return

  await bestEffortDualWrite(
    'products.updateStock',
    async () => {
      const response = await productsApi.updateStockBulk({
        updates: updates.map(({ storeId, newStock }) => ({ productId, storeId, newStock })),
      })
      const failed = failedBulkStockItems(response)
      if (failed.length > 0) {
        reportFailedStockMirrorItems(productId, failed, 'live')
        const failedStoreIds = new Set(failed.map((r) => r.storeId))
        const retryItems = updates.filter((u) => failedStoreIds.has(u.storeId))
        const retry = new Error(
          `bulk stock mirror: ${failed.length}/${updates.length} item(s) failed for product ${productId}`,
        ) as Error & { requestConfig?: unknown }
        // No `status` on this error, deliberately: it must classify as transient so the failed
        // subset is QUEUED. "Transient" here means "retryable", not "guaranteed to heal": a
        // replay that reproduces the failure is now caught by the outbox (ATL-962), which keeps
        // the entry, burns an attempt, and finally drops it LOUDLY rather than silently.
        retry.requestConfig = {
          method: 'POST',
          url: BULK_STOCK_MIRROR_URL,
          data: {
            updates: retryItems.map(({ storeId, newStock }) => ({ productId, storeId, newStock })),
          },
        }
        throw retry
      }
      return response
    },
    { firestoreId: productId },
  )
}
